import { c as _c } from "react/compiler-runtime";
import MediaTypeBadge from "@/components/drops/media/MediaTypeBadge";
import MediaDisplay from "@/components/drops/view/item/content/media/MediaDisplay";
import { useSeizeSettings } from "@/contexts/SeizeSettingsContext";
import { ApiNftLinkMediaPreviewStatusEnum } from "@/generated/models/ApiNftLinkMediaPreview";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { SingleWaveDropPosition } from "@/components/waves/drop/SingleWaveDropPosition";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { ImageScale } from "@/helpers/image.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { getDropPreviewImageUrl } from "@/helpers/waves/drop.helpers";
import Link from "next/link";
import React, { useMemo } from "react";
import { Tooltip } from "react-tooltip";
import MyStreamWaveMyVoteInput from "./MyStreamWaveMyVoteInput";
import MyStreamWaveMyVoteVotes from "./MyStreamWaveMyVoteVotes";
interface MyStreamWaveMyVoteProps {
  readonly drop: ExtendedDrop;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly isChecked?: boolean | undefined;
  readonly onToggleCheck?: ((dropId: string) => void) | undefined;
  readonly isResetting?: boolean | undefined;
  readonly isVotingClosed?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
}
type ResolvedPreviewMedia = {
  readonly url: string;
  readonly mimeType: string;
};
const DEFAULT_MIME_TYPE = "image/jpeg";
const MIME_BY_EXTENSION: Record<string, string> = {
  avif: "image/avif",
  gif: "image/gif",
  jpeg: DEFAULT_MIME_TYPE,
  jpg: DEFAULT_MIME_TYPE,
  m4v: "video/x-m4v",
  mov: "video/quicktime",
  mp3: "audio/mpeg",
  mp4: "video/mp4",
  ogg: "audio/ogg",
  ogv: "video/ogg",
  flac: "audio/flac",
  png: "image/png",
  svg: "image/svg+xml",
  wav: "audio/wav",
  webm: "video/webm",
  webp: "image/webp",
  glb: "model/gltf-binary",
  gltf: "model/gltf+json",
  usdz: "model/vnd.usdz"
};
const toNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};
const inferMimeTypeFromUrl = (url: string): string | undefined => {
  try {
    const parsed = new URL(url);
    const extensionMatch = parsed.pathname.toLowerCase().match(/\.([a-z0-9]+)$/);
    if (!extensionMatch?.[1]) {
      return undefined;
    }
    return MIME_BY_EXTENSION[extensionMatch[1]];
  } catch {
    const path = url.split("?")[0]?.toLowerCase() ?? "";
    const extensionMatch = path.match(/\.([a-z0-9]+)$/);
    if (!extensionMatch?.[1]) {
      return undefined;
    }
    return MIME_BY_EXTENSION[extensionMatch[1]];
  }
};
const resolveCurationPreviewMedia = (nftLinks: ExtendedDrop["nft_links"]): ResolvedPreviewMedia | null => {
  if (typeof nftLinks?.length !== "number" || nftLinks.length === 0) {
    return null;
  }
  for (const nftLink of nftLinks) {
    const preview = nftLink.data?.media_preview;
    const previewMimeType = toNonEmptyString(preview?.mime_type);
    const previewUrl = preview?.status === ApiNftLinkMediaPreviewStatusEnum.Ready ? toNonEmptyString(preview.card_url) ?? toNonEmptyString(preview.small_url) ?? toNonEmptyString(preview.thumb_url) : null;
    if (previewUrl) {
      return {
        url: previewUrl,
        mimeType: previewMimeType ?? inferMimeTypeFromUrl(previewUrl) ?? DEFAULT_MIME_TYPE
      };
    }
    const fallbackUrl = toNonEmptyString(nftLink.data?.media_uri);
    if (!fallbackUrl) {
      continue;
    }
    return {
      url: fallbackUrl,
      mimeType: inferMimeTypeFromUrl(fallbackUrl) ?? previewMimeType ?? DEFAULT_MIME_TYPE
    };
  }
  return null;
};
const MyStreamWaveMyVote: React.FC<MyStreamWaveMyVoteProps> = t0 => {
  const $ = _c(100);
  const {
    drop,
    onDropClick,
    isChecked: t1,
    onToggleCheck,
    isResetting: t2,
    isVotingClosed: t3,
    winningThreshold
  } = t0;
  const isChecked = t1 === undefined ? false : t1;
  const isResetting = t2 === undefined ? false : t2;
  const isVotingClosed = t3 === undefined ? false : t3;
  const {
    isCurationWave
  } = useSeizeSettings();
  let t4;
  if ($[0] !== drop.parts) {
    t4 = drop.parts.at(0)?.media.at(0);
    $[0] = drop.parts;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const artWork = t4;
  let t5;
  if ($[2] !== drop.metadata) {
    t5 = getDropPreviewImageUrl(drop.metadata);
    $[2] = drop.metadata;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const previewImageUrl = t5;
  let t6;
  if ($[4] !== artWork || $[5] !== drop.nft_links || $[6] !== drop.wave.id || $[7] !== isCurationWave) {
    t6 = !artWork && isCurationWave(drop.wave.id.toLowerCase()) ? resolveCurationPreviewMedia(drop.nft_links) : null;
    $[4] = artWork;
    $[5] = drop.nft_links;
    $[6] = drop.wave.id;
    $[7] = isCurationWave;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const curationPreviewMedia = t6;
  const resolvedMediaUrl = artWork?.url ?? curationPreviewMedia?.url ?? null;
  const resolvedMediaMimeType = artWork?.mime_type ?? curationPreviewMedia?.mimeType ?? DEFAULT_MIME_TYPE;
  const badgeMimeType = artWork?.mime_type ?? curationPreviewMedia?.mimeType;
  const isSelected = !isVotingClosed && isChecked;
  let t7;
  if ($[9] !== drop || $[10] !== onDropClick) {
    t7 = () => {
      if (window.getSelection()?.toString()) {
        return;
      }
      onDropClick(drop);
    };
    $[9] = drop;
    $[10] = onDropClick;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const handleClick = t7;
  let t8;
  if ($[12] !== drop.id || $[13] !== isVotingClosed || $[14] !== onToggleCheck) {
    t8 = e => {
      e.stopPropagation();
      if (isVotingClosed) {
        return;
      }
      if (onToggleCheck) {
        onToggleCheck(drop.id);
      }
    };
    $[12] = drop.id;
    $[13] = isVotingClosed;
    $[14] = onToggleCheck;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleCheckboxClick = t8;
  const t9 = drop.id;
  const t10 = `tw-cursor-pointer tw-rounded-xl tw-border tw-border-solid tw-bg-iron-950 tw-px-5 tw-py-4 tw-shadow-md tw-transition-all tw-duration-300 desktop-hover:hover:tw-shadow-lg ${isSelected ? "tw-border-primary-400" : "tw-border-iron-800 desktop-hover:hover:tw-border-iron-700"}`;
  let t11;
  if ($[16] !== handleCheckboxClick || $[17] !== isSelected || $[18] !== isVotingClosed) {
    t11 = !isVotingClosed && <div className="tw-mr-1 tw-flex-shrink-0 tw-self-start" onClick={handleCheckboxClick}><div className={`tw-flex tw-size-5 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid ${isSelected ? "tw-border-primary-400 tw-bg-primary-400/20" : "tw-border-iron-600 tw-bg-iron-800"} tw-cursor-pointer tw-shadow-sm tw-transition-all tw-duration-200 hover:tw-shadow-md`}>{isSelected && <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="tw-size-4 tw-text-primary-400"><path fillRule="evenodd" d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.739a.75.75 0 0 1 1.04-.208Z" clipRule="evenodd" /></svg>}</div></div>;
    $[16] = handleCheckboxClick;
    $[17] = isSelected;
    $[18] = isVotingClosed;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== previewImageUrl || $[21] !== resolvedMediaMimeType || $[22] !== resolvedMediaUrl) {
    t12 = resolvedMediaUrl && <MediaDisplay media_mime_type={resolvedMediaMimeType} media_url={resolvedMediaUrl} imageScale={ImageScale.AUTOx450} previewImageUrl={previewImageUrl} disableMediaInteraction={true} />;
    $[20] = previewImageUrl;
    $[21] = resolvedMediaMimeType;
    $[22] = resolvedMediaUrl;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12) {
    t13 = <div className="tw-relative tw-h-56 tw-w-full tw-flex-shrink-0 tw-overflow-hidden tw-bg-iron-800 md:tw-size-[106px] md:tw-min-h-[106px] md:tw-min-w-[106px]"><div className="tw-relative tw-flex tw-h-full tw-w-full tw-transform tw-items-center tw-justify-center tw-duration-300 tw-ease-out desktop-hover:hover:tw-scale-105"><div className="tw-absolute tw-inset-0 tw-z-[1]">{t12}</div></div></div>;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== badgeMimeType || $[27] !== drop.id) {
    t14 = <MediaTypeBadge mimeType={badgeMimeType} dropId={drop.id} size="sm" />;
    $[26] = badgeMimeType;
    $[27] = drop.id;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== drop.title) {
    t15 = <h3 className="tw-mb-0 tw-text-base tw-font-semibold tw-text-iron-50">{drop.title}</h3>;
    $[29] = drop.title;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t14 || $[32] !== t15) {
    t16 = <div className="tw-flex tw-items-center tw-gap-x-2">{t14}{t15}</div>;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== drop.rank) {
    t17 = typeof drop.rank === "number" && <SingleWaveDropPosition rank={drop.rank} />;
    $[34] = drop.rank;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t16 || $[37] !== t17) {
    t18 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-3">{t16}{t17}</div>;
    $[36] = t16;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== drop.author.pfp) {
    t19 = <div className="tw-relative tw-size-6 tw-flex-shrink-0 tw-overflow-hidden tw-rounded-md tw-bg-iron-800 tw-ring-1 tw-ring-white/10">{drop.author.pfp ? <img src={drop.author.pfp} alt="Profile" className="tw-h-full tw-w-full tw-bg-iron-800 tw-object-contain" /> : <div className="tw-h-full tw-w-full tw-rounded-md tw-bg-iron-800 tw-ring-1 tw-ring-white/10" />}</div>;
    $[39] = drop.author.pfp;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  const t20 = drop.author.handle ?? drop.author.id;
  const t21 = `/${drop.author.handle ?? drop.author.primary_address}`;
  let t22;
  if ($[41] !== drop.author.handle || $[42] !== drop.author.primary_address) {
    t22 = e_0 => {
      e_0.stopPropagation();
      e_0.preventDefault();
      window.open(`/${drop.author.handle ?? drop.author.primary_address}`, "_blank");
    };
    $[41] = drop.author.handle;
    $[42] = drop.author.primary_address;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== drop.author.handle || $[45] !== t21 || $[46] !== t22) {
    t23 = <Link href={t21} onClick={t22} className="tw-truncate tw-text-md tw-font-medium tw-text-iron-200 tw-no-underline tw-transition-colors tw-duration-200 desktop-hover:hover:tw-text-opacity-80 desktop-hover:hover:tw-underline">{drop.author.handle}</Link>;
    $[44] = drop.author.handle;
    $[45] = t21;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t20 || $[49] !== t23) {
    t24 = <UserProfileTooltipWrapper user={t20}>{t23}</UserProfileTooltipWrapper>;
    $[48] = t20;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  const t25 = drop.author.level || 0;
  let t26;
  if ($[51] !== t25) {
    t26 = <UserCICAndLevel level={t25} size={UserCICAndLevelSize.SMALL} />;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== t19 || $[54] !== t24 || $[55] !== t26) {
    t27 = <div className="tw-mt-4 tw-flex tw-items-center tw-gap-2">{t19}{t24}{t26}</div>;
    $[53] = t19;
    $[54] = t24;
    $[55] = t26;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== drop || $[58] !== winningThreshold) {
    t28 = <div onClick={_temp}><MyStreamWaveMyVoteVotes drop={drop} winningThreshold={winningThreshold} /></div>;
    $[57] = drop;
    $[58] = winningThreshold;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== drop.id || $[61] !== drop.top_raters) {
    let t30;
    if ($[63] !== drop.id) {
      t30 = voter => <React.Fragment key={voter.profile.id || voter.profile.primary_address}><Link href={`/${voter.profile.handle ?? voter.profile.primary_address}`} onClick={_temp2} data-tooltip-id={`my-vote-voter-${drop.id}-${voter.profile.handle ?? voter.profile.primary_address}`}>{voter.profile.pfp ? <img className="tw-h-6 tw-w-6 tw-rounded-md tw-border-2 tw-border-solid tw-border-[#111] tw-bg-iron-800 tw-object-contain" src={voter.profile.pfp} alt="Recent voter" /> : <div className="tw-h-6 tw-w-6 tw-rounded-md tw-border-2 tw-border-solid tw-border-[#111] tw-bg-iron-800" />}</Link><Tooltip id={`my-vote-voter-${drop.id}-${voter.profile.handle ?? voter.profile.primary_address}`} place="top" offset={8} opacity={1} style={{
          padding: "4px 8px",
          background: "#37373E",
          color: "white",
          fontSize: "13px",
          fontWeight: 500,
          borderRadius: "6px",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          zIndex: 99999,
          pointerEvents: "none"
        }}>{voter.profile.handle ?? voter.profile.primary_address}{" "}- {formatNumberWithCommas(voter.rating)}</Tooltip></React.Fragment>;
      $[63] = drop.id;
      $[64] = t30;
    } else {
      t30 = $[64];
    }
    t29 = drop.top_raters.slice(0, 3).map(t30);
    $[60] = drop.id;
    $[61] = drop.top_raters;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[65] !== t29) {
    t30 = <div className="tw-hidden tw-items-center -tw-space-x-2 sm:tw-flex">{t29}</div>;
    $[65] = t29;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== drop.raters_count) {
    t31 = formatNumberWithCommas(drop.raters_count);
    $[67] = drop.raters_count;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  const t32 = drop.raters_count === 1 ? "voter" : "voters";
  let t33;
  if ($[69] !== t32) {
    t33 = <span className="tw-font-normal tw-text-iron-500">{t32}</span>;
    $[69] = t32;
    $[70] = t33;
  } else {
    t33 = $[70];
  }
  let t34;
  if ($[71] !== t31 || $[72] !== t33) {
    t34 = <span className="tw-text-sm tw-font-semibold tw-text-iron-200">{t31}{" "}{t33}</span>;
    $[71] = t31;
    $[72] = t33;
    $[73] = t34;
  } else {
    t34 = $[73];
  }
  let t35;
  if ($[74] !== t30 || $[75] !== t34) {
    t35 = <div className="tw-flex tw-items-center tw-gap-2">{t30}{t34}</div>;
    $[74] = t30;
    $[75] = t34;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  let t36;
  if ($[77] !== t28 || $[78] !== t35) {
    t36 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-6 sm:tw-justify-start">{t28}{t35}</div>;
    $[77] = t28;
    $[78] = t35;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  let t37;
  if ($[80] !== drop || $[81] !== isResetting || $[82] !== isVotingClosed) {
    t37 = !isVotingClosed && <div onClick={_temp3}><MyStreamWaveMyVoteInput drop={drop} isResetting={isResetting} isVotingClosed={isVotingClosed} /></div>;
    $[80] = drop;
    $[81] = isResetting;
    $[82] = isVotingClosed;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  let t38;
  if ($[84] !== t36 || $[85] !== t37) {
    t38 = <div className="tw-mt-3.5 tw-flex tw-flex-col tw-justify-between tw-gap-4 xl:tw-mt-3 xl:tw-flex-row">{t36}{t37}</div>;
    $[84] = t36;
    $[85] = t37;
    $[86] = t38;
  } else {
    t38 = $[86];
  }
  let t39;
  if ($[87] !== t18 || $[88] !== t27 || $[89] !== t38) {
    t39 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">{t18}{t27}{t38}</div>;
    $[87] = t18;
    $[88] = t27;
    $[89] = t38;
    $[90] = t39;
  } else {
    t39 = $[90];
  }
  let t40;
  if ($[91] !== t11 || $[92] !== t13 || $[93] !== t39) {
    t40 = <div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row">{t11}{t13}{t39}</div>;
    $[91] = t11;
    $[92] = t13;
    $[93] = t39;
    $[94] = t40;
  } else {
    t40 = $[94];
  }
  let t41;
  if ($[95] !== drop.id || $[96] !== handleClick || $[97] !== t10 || $[98] !== t40) {
    t41 = <div key={t9} className={t10} onClick={handleClick}>{t40}</div>;
    $[95] = drop.id;
    $[96] = handleClick;
    $[97] = t10;
    $[98] = t40;
    $[99] = t41;
  } else {
    t41 = $[99];
  }
  return t41;
};
export default MyStreamWaveMyVote;
function _temp(e_1) {
  return e_1.stopPropagation();
}
function _temp2(e_2) {
  return e_2.stopPropagation();
}
function _temp3(e_3) {
  return e_3.stopPropagation();
}